import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useUser } from '../../contexts/TestAuthContext';
import {
  getAttorneyProfile,
  saveAttorneyProfile,
  type AttorneyProfilePayload,
} from '../../services/attorneyProfileService';

export default function MyLetterhead() {
  const { user } = useUser();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [barNumber, setBarNumber] = useState('');
  const [initialState, setInitialState] = useState({ name: '', email: '', barNumber: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [profileExists, setProfileExists] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    if (hasLoaded) return;
    let mounted = true;
    setLoading(true);
    getAttorneyProfile().then((data) => {
      if (!mounted) return;
      if (data) {
        const n = data.name || '';
        const e = data.email || '';
        const b = data.bar_number || '';
        setName(n);
        setEmail(e);
        setBarNumber(b);
        setInitialState({ name: n, email: e, barNumber: b });
        setProfileExists(true);
      } else {
        const clerkName = (user as any)?.fullName || (user as any)?.name || '';
        const clerkEmail =
          (user as any)?.primaryEmailAddress?.emailAddress || (user as any)?.email || '';
        setName(clerkName);
        setEmail(clerkEmail);
        setBarNumber('');
        setInitialState({ name: clerkName, email: clerkEmail, barNumber: '' });
        setProfileExists(false);
      }
      setLoading(false);
      setHasLoaded(true);
    });
    return () => {
      mounted = false;
    };
  }, [user, hasLoaded]);

  const dirty =
    name !== initialState.name ||
    email !== initialState.email ||
    barNumber !== initialState.barNumber;

  const handleDiscard = useCallback(() => {
    setName(initialState.name);
    setEmail(initialState.email);
    setBarNumber(initialState.barNumber);
    setError(null);
  }, [initialState]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const payload: AttorneyProfilePayload = {
        name: name.trim(),
        email: email.trim(),
        bar_number: barNumber.trim(),
      };
      const res = await saveAttorneyProfile(payload);
      const saved = res.saved;
      const n = saved.name || '';
      const e = saved.email || '';
      const b = saved.bar_number || '';
      setName(n);
      setEmail(e);
      setBarNumber(b);
      setInitialState({ name: n, email: e, barNumber: b });
      setProfileExists(true);
      setLastSavedAt(new Date());
      toast.success('Letterhead saved', {
        description:
          'Your signature block will appear on demand letters and Proofs of Loss.',
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to save letterhead';
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }, [name, email, barNumber]);

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'var(--color-bg-surface)',
    borderColor: 'var(--color-border)',
  };

  // Resting-only styling. The focus affordance is the app-wide brand ring
  // (index.css `:focus-visible` = one 2px orange outline). The component does NOT
  // also recolor the border on focus — stacking the two produced a doubled ring.
  const inputBaseStyle: React.CSSProperties = {
    backgroundColor: 'var(--color-bg-elevated)',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'var(--color-border)',
    color: 'var(--color-text-primary)',
    caretColor: 'var(--color-brand)',
  };

  return (
    <div className="mx-auto max-w-2xl">
      <div className="rounded-xl border p-6" style={cardStyle}>
        <div className="mb-1">
          <h2 className="cs-h2">My Letterhead</h2>
          <p className="cs-meta">
            The identity printed on demand letters and sworn Proofs of Loss you
            generate.
          </p>
        </div>
        <div
          className="mb-5"
          style={{ borderTop: '1px solid var(--color-border-sub)' }}
        />

        {loading ? (
          <div className="space-y-5">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-2">
                <div
                  className="h-4 w-24 animate-pulse rounded"
                  style={{ backgroundColor: 'var(--color-bg-elevated)' }}
                />
                <div
                  className="h-10 w-full animate-pulse rounded-lg"
                  style={{ backgroundColor: 'var(--color-bg-elevated)' }}
                />
              </div>
            ))}
            <div
              className="mt-6 h-40 w-full animate-pulse rounded-xl"
              style={{ backgroundColor: 'var(--color-bg-elevated)' }}
            />
          </div>
        ) : (
          <>
            {!barNumber.trim() && (
              // Signal, not wallpaper: a 3px orange left rule + orange text on the
              // base card surface — no tint fill, no full border. Severity comes from
              // the copy. (K3: reduce orange surface area, not severity.)
              <div
                className="mb-5 flex items-start gap-2.5 py-1 pl-3"
                style={{
                  borderLeft: '3px solid var(--color-brand)',
                  color: 'var(--color-needs-human-text)',
                }}
              >
                <AlertTriangle
                  className="mt-0.5 h-4 w-4 flex-shrink-0"
                  style={{ color: 'var(--color-needs-human-text)' }}
                />
                <span className="text-sm">
                  No bar number on file. Document generation is blocked until you
                  add one.
                </span>
              </div>
            )}

            <div className="space-y-5">
              {/* Attorney Name */}
              <div>
                <label
                  htmlFor="letterhead-name"
                  className="cs-label mb-1 block"
                >
                  Attorney Name
                </label>
                <input
                  id="letterhead-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={120}
                  disabled={saving}
                  className="cs-body w-full rounded-lg px-3 py-2 transition-colors"
                  style={inputBaseStyle}
                />
                <p className="cs-meta mt-1">
                  As it appears in the signature block.
                </p>
              </div>

              {/* Email — read-only, from the Clerk sign-in identity. Rendered as a plain value
                  row (not an editable-looking input) so the chrome matches "cannot edit here". */}
              <div>
                <label className="cs-label mb-1 flex items-center gap-2">
                  <span>Email</span>
                  {/* Neutral lock chip (border + muted text, NOT orange) — marks the row
                      as intentionally read-only, so the chromeless value doesn't read as a
                      broken/unfinished input. */}
                  <span
                    className="rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider"
                    style={{
                      border: '1px solid var(--color-border)',
                      color: 'var(--color-text-dim)',
                    }}
                  >
                    From sign-in
                  </span>
                </label>
                <p className="cs-body px-3 py-2" style={{ color: 'var(--color-text-secondary)' }}>
                  {email || '—'}
                </p>
                <p className="cs-meta mt-1">
                  From your sign-in identity — prints on documents. Contact an
                  admin to change.
                </p>
              </div>

              {/* State Bar Number */}
              <div>
                <label htmlFor="letterhead-bar" className="cs-label mb-1 block">
                  State Bar Number
                </label>
                <input
                  id="letterhead-bar"
                  type="text"
                  value={barNumber}
                  onChange={(e) => setBarNumber(e.target.value)}
                  maxLength={40}
                  disabled={saving}
                  placeholder="e.g., CA 123456"
                  autoFocus={!barNumber.trim()}
                  className="cs-body cs-figure w-full rounded-lg px-3 py-2 transition-colors placeholder:text-[var(--color-text-dim)]"
                  style={inputBaseStyle}
                />
                <p className="cs-meta mt-1">
                  Required — demand letters and Proofs of Loss can't be generated
                  without it.
                </p>
              </div>
            </div>

            {/* Document Preview */}
            <div
              className="mt-6 rounded-xl border p-5"
              style={{
                backgroundColor: 'var(--color-bg-surface)',
                borderColor: 'var(--color-border)',
              }}
            >
              <div className="mb-4">
                <span
                  className="cs-meta uppercase tracking-wider"
                  style={{ color: 'var(--color-text-dim)' }}
                >
                  Document Preview
                </span>
              </div>
              <div className="flex flex-col items-center text-center">
                {/* Signature line ABOVE the typed name — the convention on every
                    demand letter / sworn PoL: the wet signature sits on the rule,
                    the name is printed beneath it. */}
                <div
                  className="mb-3 w-48"
                  style={{ borderTop: '1px solid var(--color-border-sub)' }}
                />
                <p
                  style={{
                    fontFamily: '"DM Serif Display", serif',
                    fontSize: 20,
                    color: name.trim()
                      ? 'var(--color-text-primary)'
                      : 'var(--color-text-dim)',
                  }}
                >
                  {name.trim() || 'Attorney Name'}
                </p>
                <p
                  className="mt-1.5 text-xs"
                  style={{
                    fontFamily: '"DM Mono", monospace',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  {'Bar No. '}
                  <span
                    style={{
                      color: barNumber.trim()
                        ? 'var(--color-text-secondary)'
                        : 'var(--color-needs-human-text)',
                    }}
                  >
                    {barNumber.trim() || '——————'}
                  </span>
                  {' · '}
                  <span>{email || '—'}</span>
                </p>
              </div>
              <p className="cs-meta mt-4 text-center">
                Signature block as printed on every demand letter and Proof of
                Loss.
              </p>
            </div>

            {/* Footer */}
            <div
              className="mt-6 flex items-center justify-between gap-4"
              style={{ borderTop: '1px solid var(--color-border-sub)' }}
            >
              <div className="pt-4">
                {error ? (
                  <p
                    className="text-sm"
                    style={{ color: 'var(--color-blocker)' }}
                  >
                    {error}
                  </p>
                ) : dirty ? (
                  <div className="flex items-center gap-2">
                    <div
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: 'var(--color-brand)' }}
                    />
                    <span
                      className="cs-meta"
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      Unsaved changes
                    </span>
                  </div>
                ) : lastSavedAt ? (
                  <div className="flex items-center gap-2">
                    <div
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: 'var(--color-verified)' }}
                    />
                    <span
                      className="cs-meta"
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      Saved{' '}
                      <span className="font-mono">
                        {lastSavedAt.toLocaleTimeString('en-US', {
                          hour12: false,
                        })}
                      </span>
                    </span>
                  </div>
                ) : !profileExists ? (
                  <span
                    className="cs-meta"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    Profile not created yet — save to create.
                  </span>
                ) : null}
              </div>

              <div className="flex items-center gap-3 pt-4">
                {dirty && (
                  <button
                    onClick={handleDiscard}
                    disabled={saving}
                    className="cs-body rounded-lg px-4 py-2 text-sm font-medium transition-colors"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    Discard
                  </button>
                )}
                <button
                  onClick={handleSave}
                  disabled={!dirty || saving}
                  className="cs-body flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
                  style={{
                    backgroundColor:
                      dirty && !saving ? 'var(--color-brand)' : 'var(--color-bg-elevated)',
                    color:
                      dirty && !saving
                        ? 'var(--color-on-brand)'
                        : 'var(--color-text-muted)',
                  }}
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
